import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { TriangleWarning } from "components/icons/TriangleWarning";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useToast } from "hooks/useToast";
import { useUnlinkTransactionsMutation } from "store/apis/invoices";
import { BackendError } from "types/utils/error";
import { pluralize } from "utils/pluralize";

type UnlinkTransactionModalProps = {
  isOpen: boolean;
  onClose: () => void;
  invoiceId: string;
  transactionId: string;
  financialRecordType: "INVOICE" | "BILL";
};

export const UnlinkTransactionModal = ({
  isOpen,
  onClose,
  invoiceId,
  transactionId,
  financialRecordType,
}: UnlinkTransactionModalProps) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const { alertToast, successToast } = useToast();

  const [unlinkedTransaction, { isLoading: unlinkingTransaction }] =
    useUnlinkTransactionsMutation();

  const recordType = financialRecordType === "BILL" ? "bill" : "invoice";

  const confirmUnlinkTransaction = async (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!invoiceId || !transactionId) return;

    try {
      await unlinkedTransaction({
        groupId,
        entityId,
        invoiceId,
        financial_record_type: financialRecordType,
        transaction_id: transactionId,
      }).unwrap();

      successToast({
        message: "Transaction unlinked successfully",
      });

      onClose();
    } catch (error) {
      alertToast({
        message:
          (error as BackendError)?.data?.error?.message ||
          "Failed to unlink transaction",
      });
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={onClose}>
      <Modal.Content size="small" onClick={(e) => e.stopPropagation()}>
        <Modal.Body>
          <div>
            <span className="t-text-orange t-flex t-items-center t-justify-center t-w-10 t-h-10 t-rounded-full t-shadow-[0px_0px_0px_4px_rgba(255,220,78,0.15)] t-border t-border-orange-20 t-border-solid t-bg-yellow-20">
              <TriangleWarning />
            </span>
            <p className="t-mb-2 t-text-subtext t-mt-5">
              Unlink {pluralize(1, "transaction", "transactions", false)}?
            </p>
            <div className="t-text-body-sm t-text-text-30">
              Unlinking the {pluralize(1, "transaction", "transactions", false)}{" "}
              may change the {recordType} status and will update the
              corresponding transaction's categorisation.
            </div>
          </div>
        </Modal.Body>
        <Modal.FooterButtonGroup>
          <Modal.RawClose asChild>
            <Button>Cancel</Button>
          </Modal.RawClose>
          <Button
            isLoading={unlinkingTransaction}
            disabled={unlinkingTransaction}
            customType="primary"
            onClick={confirmUnlinkTransaction}
          >
            Confirm
          </Button>
        </Modal.FooterButtonGroup>
      </Modal.Content>
    </Modal.Root>
  );
};
